/**
 * Where to go back to after signing in.
 *
 * Some paths send the browser to /login with a full page load
 * (`window.location.href = '/login'` when a session has expired), which drops
 * React Router's location state — so after signing in everyone landed on the
 * dashboard, and a link into the DMS (an attachment link in an exported
 * spreadsheet, a link in an email) was lost. Remember the page first.
 */
const KEY = 'login-return-to';

/** Only same-site paths: never a scheme, host or protocol-relative "//". */
function isSafePath(p: unknown): p is string {
  return typeof p === 'string' && p.startsWith('/') && !p.startsWith('//') && !p.startsWith('/login');
}

/** Call immediately before a hard redirect to /login. */
export function rememberReturnPath(): void {
  try {
    if (typeof window === 'undefined') return;
    const here = window.location.pathname + window.location.search + window.location.hash;
    if (isSafePath(here)) sessionStorage.setItem(KEY, here);
  } catch { /* storage unavailable: fall back to the dashboard */ }
}

/** The remembered path, if any — and forget it, so it is used once. */
export function takeReturnPath(): string | null {
  try {
    const p = sessionStorage.getItem(KEY);
    sessionStorage.removeItem(KEY);
    return isSafePath(p) ? p : null;
  } catch {
    return null;
  }
}
